/**
 * Advanced Mastering Module
 * LUFS Metering, Multi-band Compression, Stereo Imaging, Export Presets
 */

// ==================== LUFS METERING ====================

/**
 * LUFS (Loudness Units Full Scale) Meter
 * Measures perceived loudness according to ITU-R BS.1770-4
 */
export class LUFSMeter {
  constructor(audioContext, sampleRate = 48000) {
    this.audioContext = audioContext;
    this.sampleRate = sampleRate;
    this.blockSize = Math.floor(sampleRate * 0.4); // 400ms blocks
    this.overlapSize = Math.floor(sampleRate * 0.1); // 100ms overlap
    
    // K-weighting filter coefficients (simplified)
    this.highShelfB = [1.53512485958697, -2.69169618940638, 1.19839281085285];
    this.highShelfA = [1, -1.69065929318241, 0.73248077421585];
    this.highPassB = [1.0, -2.0, 1.0];
    this.highPassA = [1, -1.99004745483398, 0.99007225036621];
    
    // State for integrated loudness
    this.blockLoudnessValues = [];
    this.shortTermBuffer = [];
    
    // Channel weights for stereo (L/R)
    this.channelWeights = [1.0, 1.0]; // For stereo, front L/R have weight 1.0
    
    this.reset();
  }
  
  reset() {
    this.blockLoudnessValues = [];
    this.shortTermBuffer = [];
    this.integratedLUFS = -Infinity;
    this.shortTermLUFS = -Infinity;
    this.momentaryLUFS = -Infinity;
    this.truePeakdB = -Infinity;
  }
  
  /**
   * Apply K-weighting filter (simplified implementation)
   */
  applyKWeighting(samples) {
    const filtered = new Float32Array(samples.length);
    
    // Simple high-pass approximation for K-weighting
    let prev = 0;
    for (let i = 0; i < samples.length; i++) {
      // Apply simple high-shelf and high-pass characteristics
      const weighted = samples[i] * 1.0; // Pre-filter gain
      filtered[i] = weighted - 0.01 * prev;
      prev = weighted;
    }
    
    return filtered;
  }
  
  /**
   * Calculate mean square of samples
   */
  calculateMeanSquare(samples) {
    let sum = 0;
    for (let i = 0; i < samples.length; i++) {
      sum += samples[i] * samples[i];
    }
    return sum / samples.length;
  }
  
  /**
   * Convert mean square to LUFS
   */
  meanSquareToLUFS(meanSquare) {
    if (meanSquare <= 0) return -Infinity;
    return -0.691 + 10 * Math.log10(meanSquare);
  }
  
  /**
   * Process audio buffer and update measurements
   */
  process(leftChannel, rightChannel) {
    // Apply K-weighting
    const leftWeighted = this.applyKWeighting(leftChannel);
    const rightWeighted = this.applyKWeighting(rightChannel);
    
    // Calculate mean squares
    const leftMS = this.calculateMeanSquare(leftWeighted);
    const rightMS = this.calculateMeanSquare(rightWeighted);
    
    // Sum with channel weights
    const sumMS = this.channelWeights[0] * leftMS + this.channelWeights[1] * rightMS;
    
    // Momentary loudness (instant)
    this.momentaryLUFS = this.meanSquareToLUFS(sumMS);
    
    // Store for short-term (3 second window)
    this.shortTermBuffer.push(sumMS);
    const shortTermWindow = Math.ceil(3 * this.sampleRate / leftChannel.length);
    if (this.shortTermBuffer.length > shortTermWindow) {
      this.shortTermBuffer.shift();
    }
    
    // Short-term loudness (3s window)
    if (this.shortTermBuffer.length >= shortTermWindow) {
      const avgMS = this.shortTermBuffer.reduce((a, b) => a + b, 0) / this.shortTermBuffer.length;
      this.shortTermLUFS = this.meanSquareToLUFS(avgMS);
    }
    
    // Integrated loudness (gated)
    // Only include blocks above -70 LUFS absolute threshold
    if (this.momentaryLUFS > -70) {
      this.blockLoudnessValues.push(sumMS);
      
      // Apply relative gating (-10 LU below ungated)
      if (this.blockLoudnessValues.length > 0) {
        const ungatedMS = this.blockLoudnessValues.reduce((a, b) => a + b, 0) / this.blockLoudnessValues.length;
        const ungatedLUFS = this.meanSquareToLUFS(ungatedMS);
        const relativeThreshold = ungatedLUFS - 10;
        
        const gatedBlocks = this.blockLoudnessValues.filter(ms => this.meanSquareToLUFS(ms) > relativeThreshold);
        if (gatedBlocks.length > 0) {
          const gatedMS = gatedBlocks.reduce((a, b) => a + b, 0) / gatedBlocks.length;
          this.integratedLUFS = this.meanSquareToLUFS(gatedMS);
        }
      }
    }
    
    // True peak measurement
    const maxLeft = Math.max(...leftChannel.map(Math.abs));
    const maxRight = Math.max(...rightChannel.map(Math.abs));
    const peak = Math.max(maxLeft, maxRight);
    const peakdB = 20 * Math.log10(peak || 1e-10);
    this.truePeakdB = Math.max(this.truePeakdB, peakdB);
    
    return {
      momentary: this.momentaryLUFS,
      shortTerm: this.shortTermLUFS,
      integrated: this.integratedLUFS,
      truePeak: this.truePeakdB,
    };
  }
  
  /**
   * Get current readings
   */
  getReadings() {
    return {
      momentary: this.momentaryLUFS,
      shortTerm: this.shortTermLUFS,
      integrated: this.integratedLUFS,
      truePeak: this.truePeakdB,
    };
  }
}

// ==================== MULTI-BAND COMPRESSION ====================

/**
 * Multi-band Compressor
 * 4-band dynamics processor
 */
export const createMultibandCompressor = (audioContext, options = {}) => {
  const input = audioContext.createGain();
  const output = audioContext.createGain();
  
  // Default crossover frequencies (Hz)
  const crossovers = options.crossovers || [100, 500, 2000];
  
  // Create band splitters using filters
  const bands = [];
  
  // Band 1: Low (< 100 Hz)
  const lowFilter = audioContext.createBiquadFilter();
  lowFilter.type = 'lowpass';
  lowFilter.frequency.value = crossovers[0];
  lowFilter.Q.value = 0.707;
  
  const lowCompressor = audioContext.createDynamicsCompressor();
  lowCompressor.threshold.value = options.lowThreshold ?? -24;
  lowCompressor.ratio.value = options.lowRatio ?? 4;
  lowCompressor.attack.value = options.lowAttack ?? 0.01;
  lowCompressor.release.value = options.lowRelease ?? 0.3;
  
  const lowGain = audioContext.createGain();
  lowGain.gain.value = options.lowGain ?? 1;
  
  bands.push({ filter: lowFilter, compressor: lowCompressor, gain: lowGain, name: 'Low' });
  
  // Band 2: Low-Mid (100 - 500 Hz)
  const lowMidLow = audioContext.createBiquadFilter();
  lowMidLow.type = 'highpass';
  lowMidLow.frequency.value = crossovers[0];
  
  const lowMidHigh = audioContext.createBiquadFilter();
  lowMidHigh.type = 'lowpass';
  lowMidHigh.frequency.value = crossovers[1];
  
  const lowMidCompressor = audioContext.createDynamicsCompressor();
  lowMidCompressor.threshold.value = options.lowMidThreshold ?? -20;
  lowMidCompressor.ratio.value = options.lowMidRatio ?? 3;
  lowMidCompressor.attack.value = options.lowMidAttack ?? 0.005;
  lowMidCompressor.release.value = options.lowMidRelease ?? 0.2;
  
  const lowMidGain = audioContext.createGain();
  lowMidGain.gain.value = options.lowMidGain ?? 1;
  
  bands.push({ 
    filter: lowMidLow, 
    filter2: lowMidHigh, 
    compressor: lowMidCompressor, 
    gain: lowMidGain, 
    name: 'Low-Mid' 
  });
  
  // Band 3: High-Mid (500 - 2000 Hz)
  const highMidLow = audioContext.createBiquadFilter();
  highMidLow.type = 'highpass';
  highMidLow.frequency.value = crossovers[1];
  
  const highMidHigh = audioContext.createBiquadFilter();
  highMidHigh.type = 'lowpass';
  highMidHigh.frequency.value = crossovers[2];
  
  const highMidCompressor = audioContext.createDynamicsCompressor();
  highMidCompressor.threshold.value = options.highMidThreshold ?? -18;
  highMidCompressor.ratio.value = options.highMidRatio ?? 3;
  highMidCompressor.attack.value = options.highMidAttack ?? 0.003;
  highMidCompressor.release.value = options.highMidRelease ?? 0.15;
  
  const highMidGain = audioContext.createGain();
  highMidGain.gain.value = options.highMidGain ?? 1;
  
  bands.push({ 
    filter: highMidLow, 
    filter2: highMidHigh, 
    compressor: highMidCompressor, 
    gain: highMidGain, 
    name: 'High-Mid' 
  });
  
  // Band 4: High (> 2000 Hz)
  const highFilter = audioContext.createBiquadFilter();
  highFilter.type = 'highpass';
  highFilter.frequency.value = crossovers[2];
  
  const highCompressor = audioContext.createDynamicsCompressor();
  highCompressor.threshold.value = options.highThreshold ?? -16;
  highCompressor.ratio.value = options.highRatio ?? 2;
  highCompressor.attack.value = options.highAttack ?? 0.001;
  highCompressor.release.value = options.highRelease ?? 0.1;
  
  const highGain = audioContext.createGain();
  highGain.gain.value = options.highGain ?? 1;
  
  bands.push({ filter: highFilter, compressor: highCompressor, gain: highGain, name: 'High' });
  
  // Connect signal path
  // Low band
  input.connect(lowFilter);
  lowFilter.connect(lowCompressor);
  lowCompressor.connect(lowGain);
  lowGain.connect(output);
  
  // Low-mid band
  input.connect(lowMidLow);
  lowMidLow.connect(lowMidHigh);
  lowMidHigh.connect(lowMidCompressor);
  lowMidCompressor.connect(lowMidGain);
  lowMidGain.connect(output);
  
  // High-mid band
  input.connect(highMidLow);
  highMidLow.connect(highMidHigh);
  highMidHigh.connect(highMidCompressor);
  highMidCompressor.connect(highMidGain);
  highMidGain.connect(output);
  
  // High band
  input.connect(highFilter);
  highFilter.connect(highCompressor);
  highCompressor.connect(highGain);
  highGain.connect(output);
  
  return {
    input,
    output,
    bands,
    type: 'multiband-compressor',
    setBandThreshold: (bandIndex, value) => {
      if (bands[bandIndex]?.compressor) {
        bands[bandIndex].compressor.threshold.value = value;
      }
    },
    setBandRatio: (bandIndex, value) => {
      if (bands[bandIndex]?.compressor) {
        bands[bandIndex].compressor.ratio.value = value;
      }
    },
    setBandGain: (bandIndex, value) => {
      if (bands[bandIndex]?.gain) {
        bands[bandIndex].gain.gain.value = value;
      }
    },
    getReduction: (bandIndex) => {
      return bands[bandIndex]?.compressor?.reduction || 0;
    },
    disconnect: () => {
      bands.forEach(band => {
        band.filter?.disconnect();
        band.filter2?.disconnect();
        band.compressor?.disconnect();
        band.gain?.disconnect();
      });
      input.disconnect();
      output.disconnect();
    },
  };
};

// ==================== STEREO IMAGING ====================

/**
 * Stereo Width/Imaging Processor
 */
export const createStereoImager = (audioContext, options = {}) => {
  const input = audioContext.createGain();
  const output = audioContext.createGain();
  
  // Create channel splitter and merger
  const splitter = audioContext.createChannelSplitter(2);
  const merger = audioContext.createChannelMerger(2);
  
  // Mid/Side processing gains
  const midGain = audioContext.createGain();
  const sideGain = audioContext.createGain();
  
  // Initial width (1.0 = normal, 0 = mono, 2 = wide)
  let width = options.width ?? 1.0;
  
  // Left output = (Mid + Side) / 2
  // Right output = (Mid - Side) / 2
  const leftMidGain = audioContext.createGain();
  const leftSideGain = audioContext.createGain();
  const rightMidGain = audioContext.createGain();
  const rightSideGain = audioContext.createGain();
  
  // Connect splitter
  input.connect(splitter);
  
  // Process: Left -> Mid contribution
  splitter.connect(leftMidGain, 0);
  splitter.connect(leftSideGain, 0);
  
  // Process: Right -> Mid contribution (negative for side)
  splitter.connect(rightMidGain, 1);
  splitter.connect(rightSideGain, 1);
  
  // Merge to output
  leftMidGain.connect(merger, 0, 0);
  leftSideGain.connect(merger, 0, 0);
  rightMidGain.connect(merger, 0, 1);
  rightSideGain.connect(merger, 0, 1);
  
  merger.connect(output);
  
  // Function to update width
  const updateWidth = (newWidth) => {
    width = newWidth;
    const midCoef = 1.0;
    const sideCoef = width;
    
    // Left = 0.5 * (Mid + Side * width)
    // Right = 0.5 * (Mid - Side * width)
    leftMidGain.gain.value = 0.5;
    leftSideGain.gain.value = 0.5 * sideCoef;
    rightMidGain.gain.value = 0.5;
    rightSideGain.gain.value = -0.5 * sideCoef;
  };
  
  // Initialize
  updateWidth(width);
  
  return {
    input,
    output,
    type: 'stereo-imager',
    setWidth: updateWidth,
    getWidth: () => width,
    disconnect: () => {
      splitter.disconnect();
      merger.disconnect();
      leftMidGain.disconnect();
      leftSideGain.disconnect();
      rightMidGain.disconnect();
      rightSideGain.disconnect();
      input.disconnect();
      output.disconnect();
    },
  };
};

// ==================== EXPORT PRESETS ====================

/**
 * Export preset definitions
 */
export const EXPORT_PRESETS = {
  streaming: {
    id: 'streaming',
    name: 'Streaming (Spotify/Apple)',
    description: 'Optimized for streaming platforms',
    targetLUFS: -14,
    truePeakLimit: -1,
    format: 'wav',
    sampleRate: 44100,
    bitDepth: 16,
    normalize: true,
    limiter: true,
  },
  youtube: {
    id: 'youtube',
    name: 'YouTube',
    description: 'Optimized for YouTube normalization',
    targetLUFS: -14,
    truePeakLimit: -1,
    format: 'wav',
    sampleRate: 48000,
    bitDepth: 16,
    normalize: true,
    limiter: true,
  },
  cd: {
    id: 'cd',
    name: 'CD Quality',
    description: 'Standard CD format (Red Book)',
    targetLUFS: -9,
    truePeakLimit: 0,
    format: 'wav',
    sampleRate: 44100,
    bitDepth: 16,
    normalize: false,
    limiter: true,
  },
  hifi: {
    id: 'hifi',
    name: 'Hi-Fi / Audiophile',
    description: 'Maximum quality, no limiting',
    targetLUFS: -18,
    truePeakLimit: -3,
    format: 'wav',
    sampleRate: 96000,
    bitDepth: 24,
    normalize: false,
    limiter: false,
  },
  broadcast: {
    id: 'broadcast',
    name: 'Broadcast (TV/Radio)',
    description: 'EBU R128 compliant',
    targetLUFS: -23,
    truePeakLimit: -1,
    format: 'wav',
    sampleRate: 48000,
    bitDepth: 24,
    normalize: true,
    limiter: true,
  },
  podcast: {
    id: 'podcast',
    name: 'Podcast',
    description: 'Optimized for voice clarity',
    targetLUFS: -16,
    truePeakLimit: -1,
    format: 'mp3',
    sampleRate: 44100,
    bitDepth: 16,
    normalize: true,
    limiter: true,
    mp3Quality: 192,
  },
  soundcloud: {
    id: 'soundcloud',
    name: 'SoundCloud',
    description: 'Balanced for SoundCloud streaming',
    targetLUFS: -12,
    truePeakLimit: -1,
    format: 'wav',
    sampleRate: 44100,
    bitDepth: 16,
    normalize: true,
    limiter: true,
  },
  master: {
    id: 'master',
    name: 'Master (Full Quality)',
    description: 'Uncompressed master copy',
    targetLUFS: null,
    truePeakLimit: null,
    format: 'wav',
    sampleRate: 48000,
    bitDepth: 24,
    normalize: false,
    limiter: false,
  },
};

/**
 * Get LUFS target label
 */
export const getLUFSLabel = (lufs) => {
  if (lufs === null || lufs === undefined || !isFinite(lufs)) return '--';
  return `${lufs.toFixed(1)} LUFS`;
};

/**
 * Get LUFS color based on target
 */
export const getLUFSColor = (current, target) => {
  if (!isFinite(current) || target === null) return 'text-zinc-400';
  
  const diff = Math.abs(current - target);
  if (diff <= 1) return 'text-emerald-400'; // Within 1 LU
  if (diff <= 3) return 'text-amber-400';   // Within 3 LU
  return 'text-red-400';                     // More than 3 LU off
};

export default {
  LUFSMeter,
  createMultibandCompressor,
  createStereoImager,
  EXPORT_PRESETS,
  getLUFSLabel,
  getLUFSColor,
};
